import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { confirmAsync } from '@/lib/confirm';
import { 
  Plus, Target, Users, TrendingUp, Calendar, Copy, Trash2, 
  ChevronRight, BarChart3, CheckCircle2, XCircle, Clock,
  Filter, Upload, UserPlus
} from 'lucide-react';

const CAMPAIGN_TYPES = [
  { value: 'upselling', label: 'Upselling', color: 'bg-blue-500' },
  { value: 'fidelizacion', label: 'Fidelización', color: 'bg-green-500' },
  { value: 'encuesta', label: 'Encuesta', color: 'bg-purple-500' },
  { value: 'recuperacion', label: 'Recuperación', color: 'bg-orange-500' },
  { value: 'lanzamiento', label: 'Lanzamiento', color: 'bg-pink-500' },
];

const CONTACT_STATUSES = [
  { value: 'pendiente', label: 'Pendiente', color: 'bg-gray-400' },
  { value: 'email_enviado', label: 'Email enviado', color: 'bg-blue-400' },
  { value: 'whatsapp_enviado', label: 'WhatsApp enviado', color: 'bg-green-400' },
  { value: 'llamada_realizada', label: 'Llamada realizada', color: 'bg-yellow-400' },
  { value: 'interesado', label: 'Interesado', color: 'bg-purple-400' },
  { value: 'estudio_pendiente', label: 'Estudio pendiente', color: 'bg-indigo-400' },
  { value: 'en_negociacion', label: 'En negociación', color: 'bg-orange-400' },
  { value: 'cerrado_ganado', label: 'Cerrado Ganado', color: 'bg-green-600' },
  { value: 'cerrado_perdido', label: 'Cerrado Perdido', color: 'bg-red-500' },
  { value: 'no_contesta', label: 'No contesta', color: 'bg-gray-500' },
];

export default function Campaigns({ user }) {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [includeContacts, setIncludeContacts] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    tipo: 'upselling',
    fecha_inicio: new Date().toISOString().split('T')[0],
    fecha_fin: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axiosInstance.get('/campaigns');
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Error al cargar campañas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!formData.nombre) {
      toast.error('El nombre es obligatorio');
      return;
    }

    try {
      await axiosInstance.post('/campaigns', {
        ...formData,
        fecha_inicio: new Date(formData.fecha_inicio).toISOString(),
        fecha_fin: new Date(formData.fecha_fin).toISOString()
      });
      toast.success('Campaña creada');
      setDialogOpen(false);
      setFormData({
        nombre: '',
        descripcion: '',
        tipo: 'upselling',
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_fin: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      });
      fetchCampaigns();
    } catch (error) {
      toast.error('Error al crear campaña');
    }
  };

  const handleDuplicateCampaign = async () => {
    if (!newCampaignName || !selectedCampaign) return;
    setDuplicating(true);
    try {
      const res = await axiosInstance.post(
        `/campaigns/${selectedCampaign.id}/duplicate?new_name=${encodeURIComponent(newCampaignName)}&include_contacts=${includeContacts}`
      );
      const msg = includeContacts
        ? `Campaña duplicada con ${res.data.contacts_copied} contactos`
        : 'Campaña duplicada (sin contactos)';
      toast.success(msg);
      setDuplicateDialogOpen(false);
      setNewCampaignName('');
      setSelectedCampaign(null);
      setIncludeContacts(false);
      fetchCampaigns();
    } catch (error) {
      toast.error('Error al duplicar campaña');
    } finally {
      setDuplicating(false);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!(await confirmAsync('¿Eliminar esta campaña y todos sus contactos?'))) return;
    
    try {
      await axiosInstance.delete(`/campaigns/${campaignId}`);
      toast.success('Campaña eliminada');
      fetchCampaigns();
    } catch (error) {
      toast.error('Error al eliminar campaña');
    }
  };

  const getTypeInfo = (type) => CAMPAIGN_TYPES.find(t => t.value === type) || CAMPAIGN_TYPES[0];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const endDate = new Date(dateStr);
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    return daysLeft <= 7 && daysLeft >= 0;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campañas</h1>
          <p className="text-gray-500">Gestiona campañas sobre clientes existentes</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#9C0F17] hover:bg-[#7A0C12]">
              <Plus className="w-4 h-4 mr-2" /> Nueva Campaña
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crear Nueva Campaña</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Campaña Integración Dojo 2026"
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Describe el objetivo de la campaña..."
                  className="w-full p-2 border rounded-md text-sm min-h-[80px]"
                />
              </div>
              <div>
                <Label>Tipo de Campaña</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CAMPAIGN_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={formData.fecha_inicio}
                    onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Fecha Fin</Label>
                  <Input
                    type="date"
                    value={formData.fecha_fin}
                    onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateCampaign} className="bg-[#9C0F17] hover:bg-[#7A0C12]">
                Crear Campaña
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Campañas Activas</p>
                <p className="text-2xl font-bold">{campaigns.filter(c => c.activa).length}</p>
              </div>
              <Target className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Contactos</p>
                <p className="text-2xl font-bold">{campaigns.reduce((acc, c) => acc + (c.stats?.total || 0), 0)}</p>
              </div>
              <Users className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Cerrados Ganados</p>
                <p className="text-2xl font-bold text-green-600">
                  {campaigns.reduce((acc, c) => acc + (c.stats?.cerrados_ganados || 0), 0)}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Volumen Generado</p>
                <p className="text-2xl font-bold text-blue-600">
                  {campaigns.reduce((acc, c) => acc + (c.stats?.volumen_total || 0), 0).toLocaleString('es-ES')} €
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {campaigns.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <Target className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No hay campañas</h3>
              <p className="text-gray-500 mt-1">Crea tu primera campaña para empezar</p>
            </CardContent>
          </Card>
        ) : (
          campaigns.map(campaign => {
            const typeInfo = getTypeInfo(campaign.tipo);
            const stats = campaign.stats || {};
            
            return (
              <Card 
                key={campaign.id} 
                className={`hover:shadow-lg transition-shadow cursor-pointer ${!campaign.activa ? 'opacity-60' : ''}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge className={`${typeInfo.color} text-white text-xs`}>
                          {typeInfo.label}
                        </Badge>
                        {!campaign.activa && (
                          <Badge variant="outline" className="text-gray-500">Inactiva</Badge>
                        )}
                        {isExpiringSoon(campaign.fecha_fin) && campaign.activa && (
                          <Badge className="bg-orange-500 text-white text-xs">Expira pronto</Badge>
                        )}
                        {isExpired(campaign.fecha_fin) && (
                          <Badge className="bg-red-500 text-white text-xs">Expirada</Badge>
                        )}
                      </div>
                      <CardTitle className="text-lg mt-2">{campaign.nombre}</CardTitle>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCampaign(campaign);
                          setNewCampaignName(`${campaign.nombre} (copia)`);
                          setDuplicateDialogOpen(true);
                        }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCampaign(campaign.id);
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {campaign.descripcion && (
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{campaign.descripcion}</p>
                  )}
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(campaign.fecha_inicio)} - {formatDate(campaign.fecha_fin)}
                    </span>
                  </div>

                  {/* Mini Stats */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <p className="text-lg font-bold">{stats.total || 0}</p>
                      <p className="text-xs text-gray-500">Total</p>
                    </div>
                    <div className="text-center p-2 bg-blue-50 rounded">
                      <p className="text-lg font-bold text-blue-600">{stats.contactados || 0}</p>
                      <p className="text-xs text-gray-500">Contactados</p>
                    </div>
                    <div className="text-center p-2 bg-purple-50 rounded">
                      <p className="text-lg font-bold text-purple-600">{stats.interesados || 0}</p>
                      <p className="text-xs text-gray-500">Interesados</p>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded">
                      <p className="text-lg font-bold text-green-600">{stats.cerrados_ganados || 0}</p>
                      <p className="text-xs text-gray-500">Ganados</p>
                    </div>
                  </div>

                  {/* Conversion Rate */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">Conversión</span>
                      <span className="font-medium">{stats.conversion_rate || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(stats.conversion_rate || 0, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  >
                    Ver Campaña <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicar Campaña</DialogTitle>
          </DialogHeader>
          {selectedCampaign && (
            <div className="py-4 space-y-4">
              <p className="text-sm text-gray-500">
                Duplicando: <span className="font-medium text-gray-800">{selectedCampaign.nombre}</span>
                {' '}({selectedCampaign.tipo})
              </p>
              <div>
                <Label>Nombre de la nueva campaña</Label>
                <Input
                  value={newCampaignName}
                  onChange={(e) => setNewCampaignName(e.target.value)}
                  placeholder="Nombre de la campaña"
                  data-testid="duplicate-name-input"
                />
              </div>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={includeContacts}
                  onChange={(e) => setIncludeContacts(e.target.checked)}
                  className="rounded"
                  data-testid="include-contacts-checkbox"
                />
                <div>
                  <p className="font-medium text-sm">Incluir contactos</p>
                  <p className="text-xs text-gray-500">Se copian los clientes de la campaña original (todos empiezan en estado "Pendiente")</p>
                </div>
              </label>
              <p className="text-xs text-gray-400">
                Se copian: configuracion, plantillas email/WhatsApp{includeContacts ? ', contactos con modulos inicializados' : ''}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleDuplicateCampaign} disabled={duplicating || !newCampaignName} className="bg-[#9C0F17] hover:bg-[#7A0C12]" data-testid="confirm-duplicate-btn">
              {duplicating ? 'Duplicando...' : 'Duplicar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
